import { useEffect, useState, type ReactNode } from 'react';
import { MessageSquare, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { NotificationBell } from 'notification-kit/client/index.js';
import { useRole } from '../../context/RoleContext';
import { useTheme } from '../../context/ThemeContext';
import type { Page } from '../../App';
import { useCompteMessages } from '../messages/useCompteMessages';
import { FenetreAvis } from '../avis/FenetreAvis';

import { dePersonne } from '../messages/format';
import { queNom } from '../demande/nouvelle/textesDemande';
// Topbar + sidebar + cadre de contenu (classes design-kit). Nav en français,
// items conditionnés par rôle — garde UX seulement, le serveur re-vérifie tout.
const TITRES: Record<Page, string> = {
  tableau: 'Tableau de bord',
  nouvelle: 'Nouvelle demande',
  demandes: 'Demande',
  urgence: 'Urgence',
  remplacants: 'Remplaçants',
  ateliers: 'Ateliers',
  fak: 'FRA',
  messages: 'Messages à envoyer',
  admin: 'Administration',
};

// Hauteur de la bannière « Aperçu intervenant·e » — .sidebar (design-kit) est
// aussi en position:fixed avec un `top` propre, indépendant de .shell ; il
// faut donc décaler sidebar ET shell du même montant quand la bannière est active.
const APERCU_BANDEAU_H = 44;

export function AppShell({ page, onNavigate, children }: {
  page: Page; onNavigate: (p: Page) => void; children: ReactNode;
}) {
  const { user, logout, impersonatedBy, stopImpersonating } = useAuth();
  // « en tant qu'Alexia » / « en tant que Camille » : l'élision de « que » devant le nom du compte imité.
  const nomImite = user?.name || 'ce compte';
  const { isAdmin, peutGerer, roles, apercuActif, estAdminReel, activerApercu, desactiverApercu } = useRole();
  const { theme, toggleTheme, logoPosition, toggleLogoPosition } = useTheme();
  // Menu de gauche : on le replie À LA MAIN pour voir plus large (calendrier, grandes listes). Ouvert par défaut, jamais
  // replié tout seul, et rien n'est mémorisé : l'état dure le temps de la visite (on change de page sans le perdre).
  const [menuReplie, setMenuReplie] = useState(false);
  // Deux bandeaux possibles, empilés sous la barre du haut : « Consultation en tant que … » (imitation d'un compte par un·e admin) puis « Aperçu intervenant·e ».
  const decalageBandeaux = (impersonatedBy ? APERCU_BANDEAU_H : 0) + (apercuActif ? APERCU_BANDEAU_H : 0);
  // « Votre avis » : fenêtre par-dessus la page (la page et son état ne changent pas).
  const [avisOuvert, setAvisOuvert] = useState(false);
  const lieuAvis = () => {
    const h = window.location.hash.slice(1);
    const [chemin, requete = ''] = h.split('?');
    const detail = chemin.startsWith('demandes/') ? ` n°${chemin.split('/')[1]}` : requete.includes('vue=collegues') ? ' › Demandes des collègues' : '';
    return `${TITRES[page]}${detail}`;
  };
  // Pastille « Messages à envoyer » : le nombre de lignes de la liste de la gestion (se met à jour après chaque envoi).
  const nbMessages = useCompteMessages(peutGerer, page);
  // Le titre de l'onglet du navigateur dit où l'on est (utile avec plusieurs onglets ouverts).
  useEffect(() => {
    document.title = `${TITRES[page]} — TacTac`;
  }, [page]);

  const navItem = (p: Page, label: string) => (
    <a href={`#${p}`} className={`nav-link ${page === p ? 'active' : ''}`}
       onClick={(e) => { e.preventDefault(); onNavigate(p); }}>{label}</a>
  );

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            {/* Logo : deux emplacements (design-kit), l'utilisateur choisit via
                data-logo — voir ThemeContext ; l'autre est masqué par le kit. */}
            <img className="brand-logo" src="/logo-eveil.svg" alt="Eveil" />
            TacTac<span className="brand-sep" />
            <span className="brand-page">{TITRES[page]}</span>
          </div>
        </div>
        <div className="topbar-right">
          {peutGerer && (
            <button className="btn btn-danger btn-sm" onClick={() => onNavigate('urgence')}>
              Urgence
            </button>
          )}
          {/* Les liens de notification existent sous deux formes ('#demandes/12'
              côté scheduler, '/demandes/12' côté routes) — parseHash (App.tsx)
              ne comprend ni le # ni le / initial : on normalise ICI, au seul
              point d'entrée, plutôt que de corriger 20 appels createIfEnabled. */}
          <NotificationBell onNavigate={(link) => { window.location.hash = link.replace(/^#/, '').replace(/^\//, ''); }} />
          <button className="theme-btn" onClick={toggleTheme} title="Basculer le thème">
            {theme === 'light' ? '☾' : '☀'}
          </button>
          <button
            className="theme-btn"
            onClick={toggleLogoPosition}
            title={logoPosition === 'bottom' ? 'Logo en bas à gauche — cliquer pour le placer en haut' : 'Logo en haut à gauche — cliquer pour le placer en bas'}
          >
            {logoPosition === 'bottom' ? '⤒' : '⤓'}
          </button>
          <div className="user-tag">
            <div className="user-initial">{user?.initials}</div>
            <span>{user?.name}</span>
          </div>
          {roles.filter((r) => r !== 'intervenant').map((r) =>
            r === 'admin' ? (
              <span
                key={r}
                className="badge badge-accent badge-clickable"
                onClick={() => onNavigate('admin')}
                title="Administration"
              >
                {r}
              </span>
            ) : (
              <span key={r} className="badge badge-accent">{r}</span>
            )
          )}
          {estAdminReel && !apercuActif && (
            <button className="btn btn-sm btn-ghost" onClick={activerApercu} title="Prévisualiser l'app comme un·e intervenant·e">
              Aperçu intervenant·e
            </button>
          )}
          <button className="btn btn-sm" onClick={logout}>Déconnexion</button>
        </div>
      </header>

      {impersonatedBy && (
        <div
          role="status"
          className="flex items-center justify-between gap-12"
          style={{
            position: 'fixed', top: 'var(--topbar-h)', left: 0, right: 0, zIndex: 91, height: APERCU_BANDEAU_H,
            background: 'var(--warn-dim)', borderBottom: '1px solid var(--warn)', padding: '8px 16px', boxSizing: 'border-box',
          }}
        >
          <span className="text-sm text-ink-1">
            Vous consultez TacTac en tant {queNom(nomImite).slice(0, -nomImite.length)}<b>{nomImite}</b> — connecté·e depuis le compte {dePersonne(impersonatedBy.name)}.
          </span>
          <button className="btn btn-sm btn-accent" onClick={async () => { await stopImpersonating(); window.location.reload(); }}>
            Revenir à mon compte
          </button>
        </div>
      )}

      {apercuActif && (
        // .topbar (design-kit) est en position:fixed — un simple sibling en
        // flux normal se retrouve masqué dessous. La bannière doit donc être
        // fixed elle aussi, juste sous la topbar (jamais modifier layout.css
        // du kit pour un besoin app-specific : on compense ici, localement).
        <div
          className="flex items-center justify-between gap-12"
          style={{
            position: 'fixed', top: `calc(var(--topbar-h) + ${impersonatedBy ? APERCU_BANDEAU_H : 0}px)`, left: 0, right: 0, zIndex: 90, height: APERCU_BANDEAU_H,
            background: 'var(--warn-dim)', borderBottom: '1px solid var(--warn)', padding: '8px 16px', boxSizing: 'border-box',
          }}
        >
          <span className="text-sm text-ink-1">Vous prévisualisez la vue intervenant·e — actions et menus limités en conséquence.</span>
          <button className="btn btn-sm btn-accent" onClick={desactiverApercu}>Revenir à ma vue admin</button>
        </div>
      )}

      <div className={`shell${menuReplie ? ' shell--collapsed' : ''}`} style={decalageBandeaux ? { marginTop: `calc(var(--topbar-h) + ${decalageBandeaux}px)` } : undefined}>
        <aside className="sidebar" aria-label="Menu principal" style={decalageBandeaux ? { top: `calc(var(--topbar-h) + ${decalageBandeaux}px)` } : undefined}>
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setMenuReplie((v) => !v)}
            aria-expanded={!menuReplie}
            aria-label={menuReplie ? 'Afficher le menu' : 'Réduire le menu'}
            title={menuReplie ? 'Afficher le menu' : 'Réduire le menu'}
          >
            {menuReplie ? <PanelLeftOpen className="icon-sm" /> : <><PanelLeftClose className="icon-sm" /> Réduire le menu</>}
          </button>
          <div className="sidebar-block">
            {navItem('tableau', 'Tableau de bord')}
            {navItem('nouvelle', 'Nouvelle demande')}
            {peutGerer && (
              <a href="#messages" className={`nav-link ${page === 'messages' ? 'active' : ''}`}
                 aria-label={nbMessages ? `Messages à envoyer, ${nbMessages} en attente` : 'Messages à envoyer'}
                 onClick={(e) => { e.preventDefault(); onNavigate('messages'); }}>
                Messages à envoyer
                {nbMessages != null && nbMessages > 0 && <span className="nav-compte" aria-hidden="true">{nbMessages}</span>}
              </a>
            )}
          </div>
          <div className="sidebar-rule" />
          <div className="sidebar-block">
            <div className="sidebar-label">Ressources</div>
            {peutGerer && navItem('urgence', 'Urgence')}
            {navItem('remplacants', 'Remplaçants')}
            {navItem('ateliers', 'Ateliers')}
            {navItem('fak', 'FRA')}
          </div>
          {isAdmin && (
            <>
              <div className="sidebar-rule" />
              <div className="sidebar-block">
                <div className="sidebar-label">Système</div>
                {navItem('admin', 'Administration')}
              </div>
            </>
          )}
          <div className="sidebar-rule" />
          <div className="sidebar-block avis-bloc">
            <button type="button" className="nav-link avis-nav" aria-haspopup="dialog" onClick={() => setAvisOuvert(true)}
              title="Votre avis sur l'application">
              <MessageSquare className="icon-sm" aria-hidden="true" /><span>Votre avis</span>
            </button>
          </div>
          <div className="sidebar-footer">
            <img className="sidebar-logo" src="/logo-eveil.svg" alt="Eveil" />
          </div>
        </aside>
        {/* La clé force un remontage complet au basculement de l'aperçu : toutes les
            pages rechargent leurs données avec les droits de la nouvelle vue (sinon une
            fiche ouverte garde les boutons/données de la vue précédente). */}
        {avisOuvert && <FenetreAvis lieu={lieuAvis()} onFermer={() => setAvisOuvert(false)} />}
        <main className="content" key={apercuActif ? 'apercu-intervenant' : 'vue-reelle'}>{children}</main>
      </div>
    </>
  );
}
